import { useEffect, useRef, type RefObject } from 'react'
import { DatePicker } from '~/components/ui/datePicker.tsx'
import {
  Button,
  buttonVariants,
} from '~/components/ui/inputs/button/Button.tsx'
import { TimeZonesSelect } from '~/components/ui/inputs/select/timezones-select.tsx'
import { ScrollArea } from '~/components/ui/scroll-area.tsx'
import { Spacer } from '~/components/ui/spacer.tsx'
import { cn } from '~/utils/misc.tsx'
import { dateToFriendly } from '~/utils/strings.ts'

type AvailableHour = {
  time24h: string
  time12h: string
}

const HEADER_OFFSET = 64
const BOTTOM_PADDING = 24

function findStickyAncestor(
  element: HTMLElement,
  boundary?: HTMLElement | null,
): HTMLElement | null {
  let node: HTMLElement | null = element.parentElement
  while (node && node !== boundary && node !== document.body) {
    if (window.getComputedStyle(node).position === 'sticky') return node
    node = node.parentElement
  }
  return null
}

function scrollSubmitIntoView(
  element: HTMLElement,
  container?: HTMLElement | null,
) {
  const viewport = window.innerHeight
  const rect = element.getBoundingClientRect()

  const isVisible =
    rect.top >= HEADER_OFFSET && rect.bottom <= viewport - BOTTOM_PADDING
  if (isVisible) return

  const sticky = findStickyAncestor(element, container)

  // Outside a sticky panel (mobile column, dialog) the button moves together
  // with its scroll container, so the native behaviour is enough.
  if (!sticky || !container) {
    element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    return
  }

  // While the panel is pinned, scrolling the page does not move it: the button
  // only comes into view once the panel reaches the bottom of its container,
  // so scroll straight to the position where that happens.
  const stickyRect = sticky.getBoundingClientRect()
  const containerBottom =
    window.scrollY + container.getBoundingClientRect().bottom
  const buttonOffsetInPanel = rect.bottom - stickyRect.top

  const maxScroll = Math.max(
    0,
    document.documentElement.scrollHeight - viewport,
  )
  const target = Math.min(
    maxScroll,
    Math.max(
      0,
      containerBottom -
        sticky.offsetHeight +
        buttonOffsetInPanel -
        viewport +
        BOTTOM_PADDING,
    ),
  )

  if (Math.abs(target - window.scrollY) < 1) return

  window.scrollTo({ top: target, behavior: 'smooth' })
}

type Props = {
  disabledDates: Date[]
  selectedDate?: Date
  selectedTime?: string
  availableHours: AvailableHour[]
  timeZone: string
  setFormatMonthAndYear: (date: Date) => void
  setSelectedDate: (date: Date | undefined) => void
  setSelectedTime: (time: string) => void
  setTimeZone: (value: string) => void
  onSubmit: (selectedDate: Date, selectedTime: string, timeZone: string) => void
  title?: string
  submitLabel?: string
  calendarVariant?: 'default' | 'large'
  scrollContainerRef?: RefObject<HTMLElement>
}

export function ServiceBookingPanel({
  disabledDates,
  selectedDate,
  selectedTime,
  availableHours,
  timeZone,
  setFormatMonthAndYear,
  setSelectedDate,
  setSelectedTime,
  setTimeZone,
  onSubmit,
  title = 'Seleccione fecha y hora',
  submitLabel = 'Reservar',
  calendarVariant = 'default',
  scrollContainerRef,
}: Props) {
  const submitButtonRef = useRef<HTMLButtonElement>(null)
  const isLargeCalendar = calendarVariant === 'large'

  useEffect(() => {
    if (!selectedDate) return

    const element = submitButtonRef.current
    if (!element || !element.offsetParent) return

    const rect = element.getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0) return

    scrollSubmitIntoView(element, scrollContainerRef?.current)
  }, [selectedDate, selectedTime, availableHours.length, scrollContainerRef])

  const largeCalendarClassNames = {
    months:
      'flex w-full flex-col justify-center space-y-0 sm:flex-row sm:space-x-0 sm:space-y-0',
    month: 'mx-auto w-fit max-w-full space-y-2',
    caption: 'relative flex items-center justify-center pb-0 pt-0',
    caption_label: 'text-base font-semibold',
    nav_button: cn(
      buttonVariants({ variant: 'outline' }),
      'h-9 w-9 bg-transparent p-0 opacity-50 hover:opacity-100',
    ),
    table: 'mx-auto w-fit border-collapse',
    head_row: 'mt-1 flex',
    head_cell:
      'text-foreground-muted w-10 shrink-0 rounded-md text-center font-normal text-[0.8rem] sm:w-11 sm:text-[0.85rem]',
    row: 'mt-2 flex',
    day: cn(
      buttonVariants({ variant: 'ghost' }),
      'h-10 w-10 p-0 text-[1.05rem] font-normal aria-selected:opacity-100 sm:h-11 sm:w-11 sm:text-base',
    ),
    cell: 'text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
  }

  return (
    <div className={cn(isLargeCalendar && 'flex w-full min-w-0 flex-col')}>
      <h3 className="text-center text-h6 text-secondary-header sm:text-h5">
        {title}
      </h3>
      <Spacer size={isLargeCalendar ? '5xs' : '4xs'} />
      <div
        className={cn(
          'flex w-full justify-center overflow-x-auto',
          isLargeCalendar ? 'py-1' : '',
        )}
      >
        <DatePicker
          fromDate={new Date()}
          mode="single"
          selected={selectedDate}
          onSelect={date => setSelectedDate(date)}
          disabled={disabledDates}
          showOutsideDays={false}
          onMonthChange={event => setFormatMonthAndYear(event)}
          className={cn(
            'p-3',
            isLargeCalendar &&
              'mx-auto w-fit max-w-full px-1 py-2 sm:px-2 sm:py-3',
          )}
          classNames={isLargeCalendar ? largeCalendarClassNames : undefined}
        />
      </div>
      <Spacer size="5xs" />
      <TimeZonesSelect
        labelProps={{
          children: 'Zona horaria',
          className: 'font-bold',
        }}
        className="w-full min-w-0"
        inputProps={{
          value: timeZone,
          onChange: event => {
            if (typeof event === 'string') return
            setTimeZone(event.target.value)
          },
          optionsClassName: 'w-full',
        }}
      />

      {selectedDate && (
        <>
          <Spacer size="4xs" />
          <div>
            <p className="text-sm font-semibold text-secondary">
              {dateToFriendly(selectedDate)}
            </p>
            <Spacer size="5xs" />
            <ScrollArea className="h-[180px]">
              <div className="flex flex-col gap-2 pr-2">
                {availableHours.map(({ time24h, time12h }, i) => (
                  <div key={selectedDate.getTime() + time24h + i}>
                    <Button
                      variant={selectedTime === time24h ? 'primary' : 'outline'}
                      className="w-full"
                      onClick={() => setSelectedTime(time24h)}
                    >
                      {time12h}
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
            <Spacer size="5xs" />
            <Button
              ref={submitButtonRef}
              className="w-full"
              disabled={!selectedTime}
              onClick={() =>
                selectedDate &&
                selectedTime &&
                onSubmit(selectedDate, selectedTime, timeZone)
              }
            >
              {submitLabel}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
